import { useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { CaseId } from '@casetrace/shared';
import { createWhatsappUpload, startImport, uploadSourceFile } from '../../lib/callables';
import { Button } from '../../components/ui/button';
import { SourceTypeIcon } from './source-type-icon';

export function WhatsappUploadButton({ caseId }: { readonly caseId: CaseId }) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!file.name.toLowerCase().endsWith('.zip')) {
        throw new Error('Kun ZIP-filer støttes for WhatsApp-import.');
      }
      const upload = await createWhatsappUpload({ caseId, navn: 'WhatsApp', fileName: file.name });
      await uploadSourceFile({
        caseId,
        sourceId: upload.sourceId,
        uploadObjectPath: upload.uploadObjectPath,
        file,
      });
      return startImport({ caseId, sourceId: upload.sourceId });
    },
  });

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    uploadMutation.mutate(file);
    event.currentTarget.value = '';
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".zip,application/zip"
        className="hidden"
        onChange={handleFileSelected}
      />
      <Button
        className="mt-2"
        variant="outline"
        disabled={uploadMutation.isPending}
        onClick={() => fileInputRef.current?.click()}
      >
        <SourceTypeIcon type="whatsapp" />
        Last opp WhatsApp ZIP
      </Button>
      {uploadMutation.isPending && (
        <p className="mt-2 text-xs text-text-tertiary">Laster opp ZIP og starter import...</p>
      )}
      {uploadMutation.error && (
        <p className="mt-2 text-xs text-danger/80">
          Kunne ikke importere WhatsApp ZIP. Prøv igjen.
        </p>
      )}
    </>
  );
}
